'use client';

import { useCallback } from 'react';

import { usePlanAccess } from 'src/hooks/use-plan-access';

import { IPlanService } from 'src/constants/plan-service';
import { usePlanUpgrade } from 'src/contexts/plan-upgrade-context';

import { useKeywordPlanUsageModel } from 'src/sections/workspace/hooks/model/use-keyword-plan-usage-model';

/**
 * **수집 세트에 무언가를 더하기 전의 관문** — 플랜이 허락한 칸(`keyword.keywordCount`)을 본다.
 *
 * 🚨 **서버는 플랜을 안 본다.** `keywordCampaign.createMany` 에는 문서 크기 상한만 있고
 *   플랜 판정이 없다 — 즉 **화면이 유일한 문지기**다. 세트를 만드는 길이 여럿인데 그중 한 곳만
 *   관문을 지나면 나머지 길로 얼마든지 넘긴다(실제로 그랬다 · 브랜드 허브 「수집 세트로 등록」).
 * ⛔ **새로 세는 코드를 짜지 마라** — 쓰는 수는 워크스페이스 문서의 파생 필드
 *   (`keywords` + `accounts`)이고 정본은 `useKeywordPlanUsageModel` 이다.
 * ⚠️ 칸은 **키워드와 계정을 함께** 센다 — 「조회어 3 + 계정 2」는 다섯 칸이다.
 * ⚠️ 화면이 그리는 칸(`utils/plan/track-capacity` · 살아 있는 세트)과 셈의 출처가 다르다 — 파생 칸이 늦으면 갈린다(A675③).
 */
export function useKeywordSlotGuard() {
  const { workspace } = usePlanAccess();
  const { usedKeywordCount } = useKeywordPlanUsageModel(workspace);
  const { checkAndShow } = usePlanUpgrade();

  /**
   * 더해도 되는지 묻는다.
   * @param adding 이번에 더할 칸 수(키워드 + 계정)
   * @returns **막혔으면 참** — 업그레이드 안내가 이미 떴으므로 부르는 쪽은 그대로 돌아선다
   */
  const guardAdd = useCallback(
    (adding: number): boolean => {
      const want = Math.max(0, Number(adding) || 0);
      if (want === 0) return false;
      return checkAndShow(IPlanService.keyword.keywordCount, usedKeywordCount + want);
    },
    [checkAndShow, usedKeywordCount],
  );

  return { guardAdd, usedKeywordCount };
}
